import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { Button as AriaButton, Link } from 'react-aria-components';
import { useCreateWorkspace, useWorkspaces } from '../api/queries';
import { useSession } from '../app/session-context';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { InputField } from '../ui/Input';
import { Menu, MenuItem } from '../ui/Menu';

const workspaceHref = (id: string): string => `/w/${encodeURIComponent(id)}/reports`;

/**
 * Creates a workspace, then goes to it. Navigation goes through the UI
 * provider (a Link click); this never touches the session store, because the
 * workspace route's `beforeLoad` enters the workspace.
 */
function NewWorkspaceDialog({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }): ReactElement {
  const create = useCreateWorkspace();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const hop = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (createdId) hop.current?.click();
  }, [createdId]);

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (create.isPending || name.trim() === '') return;
    setError(null);
    try {
      const created = await create.mutateAsync(name.trim());
      onOpenChange(false);
      setName('');
      setCreatedId(created.id);
    } catch (err) {
      // The server's message is shown as is: unknown codes decode as INTERNAL.
      setError(err instanceof Error ? err.message : 'Could not create the workspace.');
    }
  }

  return (
    <>
      <Dialog
        title="New workspace"
        size="sm"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        footer={
          <>
            <Button variant="secondary" onPress={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" busy={create.isPending} onPress={() => void submit()}>
              Create
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} className="m-0 p-0">
          <InputField label="Workspace name" value={name} onChange={setName} error={error ?? undefined} />
        </form>
      </Dialog>
      {createdId ? (
        <Link ref={hop} href={workspaceHref(createdId)} aria-hidden="true" className="hidden">
          Open
        </Link>
      ) : null}
    </>
  );
}

const TRIGGER =
  'flex items-center gap-2 m-0 px-3 py-2 border border-solid border-on-nav-line rounded-control bg-transparent text-on-nav-strong font-ui text-md appearance-none cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong';

/**
 * The workspace picker. Accounts mode: a menu button named by the current
 * workspace. Admin mode: the name as text. `badge` replaces the trigger's
 * content (the rail's initials badge).
 */
export function WorkspaceSwitcher({
  workspaceId,
  variant,
  badge
}: {
  workspaceId: string;
  variant: 'full' | 'rail';
  badge?: (name: string) => ReactNode;
}): ReactElement {
  const { snapshot } = useSession();
  const workspaces = useWorkspaces();
  const [creating, setCreating] = useState(false);
  const name = workspaces.data?.find((w) => w.id === workspaceId)?.name ?? 'Workspace';
  const content = variant === 'rail' && badge ? badge(name) : <span className="grow text-left">{name}</span>;

  if (snapshot.mode === 'admin') {
    return variant === 'rail' && badge ? <>{badge(name)}</> : <span className="px-3 py-2 text-md text-on-nav-strong">{name}</span>;
  }

  const trigger = (
    <AriaButton aria-label={name} className={variant === 'rail' ? 'm-0 p-0 border-0 bg-transparent cursor-pointer' : TRIGGER}>
      {content}
      {variant === 'full' ? <Icon name="chevron" size={14} /> : null}
    </AriaButton>
  );

  return (
    <>
      <Menu label="Workspaces" trigger={trigger}>
        {(workspaces.data ?? []).map((w) => (
          <MenuItem key={w.id} id={w.id} href={workspaceHref(w.id)}>
            <span className="flex items-center gap-2">
              <span className="grow">
                {w.name}
                {w.id === workspaceId ? (
                  <>
                    {' '}
                    <span className="sr-only">(current)</span>
                  </>
                ) : null}
              </span>
              {w.id === workspaceId ? <Icon name="check" size={14} /> : <span className="inline-block w-menu-check" aria-hidden="true" />}
            </span>
          </MenuItem>
        ))}
        <MenuItem id="new-workspace" onAction={() => setCreating(true)}>
          New workspace
        </MenuItem>
      </Menu>
      <NewWorkspaceDialog isOpen={creating} onOpenChange={setCreating} />
    </>
  );
}
